import { useState } from "react";
import {
  Avatar,
  Button,
  CheckIcon,
  Combobox,
  Group,
  Modal,
  Pill,
  PillsInput,
  ScrollArea,
  Text,
  TextInput,
  useCombobox,
} from "@mantine/core";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import "./PaymentRequestModal.scss";
import { AppResources } from "@/constants";
import {
  formatCurrencyInput,
  getInitials,
  paymentInputFieldLabels,
  paymentModalTitle,
} from "../../../utils";
import { createPaymentRequest } from "@/utils/api/payments";
import { useSearch } from "@/utils/hooks/useSearch";

const MAX_DESCRIPTION_LENGTH = 50;

interface PaymentRequestModalProps {
  opened: boolean;
  onClose: () => void;
}

interface User {
  id: string;
  name: string;
  email: string;
}

const PaymentRequestModal = ({ opened, onClose }: PaymentRequestModalProps) => {
  // Confirmed payment state
  const [confirmed, setConfirmed] = useState(false);
  // Error state for failed requests
  const [error, setError] = useState<string | null>(null);
  // Loading state for the request
  const [isLoading, setIsLoading] = useState(false);
  // React Query client for invalidating queries
  const queryClient = useQueryClient();

  // Combobox state
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => combobox.updateSelectedOptionIndex("active"),
  });
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState<string | null>(null);

  // Payment form fields
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");

  // Fetch clients data
  const { clients, isLoadingClients, isErrorClients } = useSearch();

  // Transform clients into the shape the combobox needs
  const transformedClients: User[] = (clients || []).map((client) => ({
    id: client.userId,
    name: `${client.firstName} ${client.lastName}`,
    email: client.email,
  }));

  // Filter transformed clients by name or email for the search
  const userOptions = transformedClients
    .filter(
      (user) =>
        user.name.toLowerCase().includes(search.trim().toLowerCase()) ||
        user.email.toLowerCase().includes(search.trim().toLowerCase())
    )
    .map((user) => (
      <Combobox.Option
        value={user.email}
        key={user.email}
        active={selectedUser === user.email}
        className="combobox-option"
      >
        <Group gap="md" p="xs">
          {selectedUser === user.email ? <CheckIcon size={12} /> : null}
          <Avatar size="sm" radius="xl" p="sm" bg="#F16E00" color="white">
            {getInitials(user.name)}
          </Avatar>
          <div className="payment-request-modal-user-info">
            <Text>{user.name}</Text>
            <Text size="xs" c="dimmed">
              {user.email}
            </Text>
          </div>
        </Group>
      </Combobox.Option>
    ));

  // Get the selected user object (for client_id)
  const selectedUserObj = transformedClients.find(
    (user) => user.email === selectedUser
  );

  // Handle user selection
  const handleUserSelect = (val: string) => {
    setSelectedUser(val);
    setSearch("");
    combobox.closeDropdown();
  };

  // Handle removing a user
  const handleUserRemove = () => {
    setSelectedUser(null);
    setSearch("");
  };

  // Submit the payment request
  const handleCreatePayment = async () => {
    if (!selectedUserObj || !amount || !description) {
      alert("Please select a user and fill out amount & description.");
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      await createPaymentRequest({
        client_id: selectedUserObj.id,
        amount: Number(amount) * 100, // Convert to cents
        description,
      });

      // Invalidate the payments query so PaymentTable refetches new data
      queryClient.invalidateQueries({
        queryKey: ["payments"],
      });

      // On success, show confirmation screen
      setConfirmed(true);
    } catch (err: any) {
      let errorMessage = "An unexpected error occurred. Please try again.";
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        if (status === 401) {
          errorMessage = "Authentication failed. Please log in again.";
        } else if (detail) {
          errorMessage = detail;
        } else if (err.message) {
          errorMessage = err.message;
        }
        console.error(
          "Error creating payment request:",
          err.response?.data || err.message
        );
      } else {
        console.error("Error creating payment request:", err);
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Reset modal when closing so next time it opens fresh
  const handleClose = () => {
    setConfirmed(false);
    setError(null);
    setSelectedUser(null);
    setSearch("");
    setDescription("");
    setAmount("");
    onClose();
  };

  // If desired, you can display a loading or error state while fetching clients
  if (isLoadingClients) {
    return (
      <Modal opened={opened} onClose={handleClose} title="Loading">
        <div>Loading clients...</div>
      </Modal>
    );
  }
  if (isErrorClients) {
    return (
      <Modal opened={opened} onClose={handleClose} title="Error">
        <div>Failed to load clients.</div>
      </Modal>
    );
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={!confirmed && !error && paymentModalTitle("Request Payment")}
      size="lg"
    >
      <div className="payment-request-modal-form">
        {confirmed ? (
          <div className="payment-request-modal-confirmed">
            <img src={AppResources.requestConfirmation} alt="Confirmed" />
            <Text fw={600} size="xl">
              Payment Requested
            </Text>
            <Text>
              Your client has been notified of your payment request.
              <br /> Check back on the <strong>Payments</strong> page for
              payment updates.
            </Text>
          </div>
        ) : error ? (
          <div className="payment-request-modal-error">
            <Text fw={600} size="xl" c="red">
              Payment Request Failed
            </Text>
            <Text c="dimmed" ta="center" mt="md">
              {error}
            </Text>
            <Button
              color="#0077CC"
              size="md"
              radius="md"
              mt="xl"
              onClick={() => setError(null)}
            >
              Try Again
            </Button>
          </div>
        ) : (
          <>
            {/* Request Payment From Field (Combobox) */}
            <div className="combobox-container">
              {paymentInputFieldLabels("Request payment from", "")}
              <Combobox store={combobox} onOptionSubmit={handleUserSelect}>
                <Combobox.DropdownTarget>
                  <PillsInput
                    variant={selectedUser ? "transparent" : "default"}
                    size="md"
                    radius="md"
                  >
                    <Pill.Group>
                      {selectedUser && selectedUserObj ? (
                        <Pill
                          withRemoveButton
                          onRemove={handleUserRemove}
                          size="xl"
                          bg="#F9C599"
                        >
                          <div className="payment-pill-content">
                            <Avatar
                              size="sm"
                              radius="xl"
                              p="sm"
                              bg="#F16E00"
                              color="white"
                            >
                              {getInitials(selectedUserObj.name)}
                            </Avatar>
                            <Text>{selectedUserObj.name}</Text>
                          </div>
                        </Pill>
                      ) : (
                        <Combobox.EventsTarget>
                          <PillsInput.Field
                            onFocus={() => combobox.openDropdown()}
                            onBlur={() => combobox.closeDropdown()}
                            value={search}
                            placeholder="Enter name or email address"
                            onChange={(event) => {
                              combobox.updateSelectedOptionIndex();
                              setSearch(event.currentTarget.value);
                            }}
                            onKeyDown={(event) => {
                              if (
                                event.key === "Backspace" &&
                                search.length === 0 &&
                                selectedUser
                              ) {
                                event.preventDefault();
                                handleUserRemove();
                              }
                            }}
                          />
                        </Combobox.EventsTarget>
                      )}
                    </Pill.Group>
                  </PillsInput>
                </Combobox.DropdownTarget>

                <Combobox.Dropdown>
                  <ScrollArea h={250} type="always">
                    <Combobox.Options w={500} maw={500}>
                      {userOptions.length > 0 ? (
                        userOptions
                      ) : (
                        <Combobox.Empty>Nothing found...</Combobox.Empty>
                      )}
                    </Combobox.Options>
                  </ScrollArea>
                </Combobox.Dropdown>
              </Combobox>
            </div>

            {/* Payment Description Field */}
            <TextInput
              placeholder="Enter payment description"
              size="md"
              radius="md"
              label={paymentInputFieldLabels(
                "Description",
                "Provide a payment description. This will be shown to your clients at the time of payment."
              )}
              required
              withAsterisk={false}
              value={description}
              maxLength={MAX_DESCRIPTION_LENGTH} // Restricts input at the HTML level
              onChange={(e) => {
                const trimmedValue = e.currentTarget.value.slice(
                  0,
                  MAX_DESCRIPTION_LENGTH
                );
                setDescription(trimmedValue);
              }}
            />

            {/* Payment Amount Field */}
            <div>
              {paymentInputFieldLabels("Payment Amount", "")}
              <Group gap="sm">
                <Text fw={600} size="xl" c="dimmed">
                  $
                </Text>
                <TextInput
                  placeholder="0.00"
                  variant="unstyled"
                  size="xl"
                  radius="md"
                  required
                  withAsterisk={false}
                  className="payment-amount-input"
                  value={amount}
                  onChange={(e) =>
                    setAmount(formatCurrencyInput(e.currentTarget.value))
                  }
                />
              </Group>
            </div>

            {/* Submit Button */}
            <div className="payment-request-modal-footer">
              <Text c="dimmed" size="sm">
                SmartWiz applies a 3.9% + $5 service fee <br />
                to every payment transaction.
              </Text>
              <Button
                color="#0077CC"
                size="md"
                radius="md"
                onClick={handleCreatePayment}
                loading={isLoading}
              >
                Request
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default PaymentRequestModal;
